import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Alert,
  Checkbox,
  FormControlLabel,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/currency';

interface CloseOrderModalProps {
  open: boolean;
  onClose: () => void;
  orderId: number;
  orderAmount: number;
  commissionRate: number;
  onCloseOrder: (reason: string) => Promise<void>;
}

const CloseOrderModal: React.FC<CloseOrderModalProps> = ({
  open,
  onClose,
  orderId,
  orderAmount,
  commissionRate,
  onCloseOrder
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const commissionAmount = orderAmount * commissionRate;

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError(t('please_enter_reason'));
      return;
    }

    if (!isConfirmed) {
      setError(t('please_confirm_cancellation'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await onCloseOrder(reason);
      handleClose();
    } catch (err: any) {
      setError(err.message || t('failed_to_close_order'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setIsConfirmed(false);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('close_order_with_commission')} #{orderId}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              {t('cancellation_warning_title')}
            </Typography>
            <Typography variant="body2">
              {t('cancellation_warning_description')}
            </Typography>
          </Alert>

          <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              {t('financial_details')}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">{t('order_amount')}:</Typography>
              <Typography variant="body1" fontWeight="bold">
                {formatCurrency(orderAmount)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">{t('cancellation_commission_rate')}:</Typography>
              <Typography variant="body1" fontWeight="bold">
                {(commissionRate * 100).toFixed(1)}%
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">{t('cancellation_commission')}:</Typography>
              <Typography variant="body1" fontWeight="bold" color="error">
                -{formatCurrency(commissionAmount)}
              </Typography>
            </Box>
          </Box>

          <TextField
            autoFocus
            margin="dense"
            name="reason"
            label={t('cancellation_reason')}
            fullWidth
            multiline
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={isLoading}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={isConfirmed}
                onChange={(e) => setIsConfirmed(e.target.checked)}
                disabled={isLoading}
              />
            }
            label={
              <Typography variant="body2">
                {t('i_understand_commission_will_be_charged')}
              </Typography>
            }
            sx={{ mt: 2 }}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          {t('cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="error"
          disabled={isLoading || !reason.trim() || !isConfirmed}
        >
          {isLoading ? <CircularProgress size={24} /> : t('confirm_cancellation')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CloseOrderModal;
